'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { currentVehicleIdAtom } from '@core/Atoms/Vehicle/CurrentVehicleId.atom';
import { isInstaMotion } from '@core/Utils/Vehicle/IsInstaMotion';
import { isVtiVehicle } from '@core/Utils/Vehicle/IsVtiVehicle';
import Button from '@gds/Button/Button';
import { Image as ImageIcon } from '@gds/Icons/Paths/Image';
import { Video } from '@gds/Icons/Paths/Video';
import { VideoReq } from '@gds/Icons/Paths/VideoReq';
import { Typography } from '@gds/Typography/Typography';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { generateLeadTrackingData } from '@lead-forms/Tracking/LeadForms.tracking';
import { Image360 } from '@shared-components/Image360/Image360';
import { View360 } from '@shared-components/Image360/View360.icon';
import { useImageCarousel } from '@shared-components/ImageCarousel/Hooks/UseImageCarousel';
import { ImageCarousel } from '@shared-components/ImageCarousel/ImageCarousel';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { shouldRequestVideoAtom } from '../../Atoms/ShouldRequestVideo.atom';
import {
  FALLBACK_IMAGE,
  IMAGES_360_TAB_NAME,
  IMAGES_TAB_NAME,
  VIDEO_TAB_NAME,
} from './Gallery.constants';
import type { GalleryProps, Tab } from './Gallery.entity';
import GalleryContainer from './GalleryContainer';
import PatchworkGallery from './PatchworkGallery/PatchworkGallery';
import SlideshowGallery from './SlideshowGallery/SlideshowGallery';

import styles from './Gallery.module.css';

const Gallery = ({
  images,
  imageCarouselStrings,
  galleryStrings,
  video,
  images360,
  tenant,
  isStockUnavailable,
  comingSoonSlot,
}: GalleryProps) => {
  const [step, setStep] = useState(0);
  const [currentClickImgIndex, setCurrentClickImgIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<Tab>(IMAGES_TAB_NAME);
  const [hasRequestCtas, setHasReqestCtas] = useState(false);
  const shouldRequestVideo = useAtomValue(shouldRequestVideoAtom);
  const toggleLeadForm = useSetAtom(activeLeadFormAtom);
  const userId = useAtomValue(userIdAtom);
  const listingId = useAtomValue(currentVehicleIdAtom);
  const formState = useAtomValue(formStateAtom);
  const vehicle = useAtomValue(currentVehicleAtom)!;
  const searchParams = useAtomValue(searchParamsAtom);
  const { threeSixty: hasThreeSixty } = useAtomValue(flagsAtom);
  const carouselRef = useRef<HTMLDivElement>(null);
  const carouselProps = useImageCarousel(carouselRef);

  const { backButton: _, ...tabStrings } = galleryStrings;
  const totalImgs = images.length;

  const has360Images = !!images360?.length && hasThreeSixty;
  const hasVideo = !!video?.[0]?.link;
  const is360Priority = has360Images && images.length < 2;
  const shouldRequestImages = totalImgs === 0;

  const isVti = isVtiVehicle(vehicle?.details?.dataSource);
  const isInstaMotionVehicle = isInstaMotion(vehicle?.details?.dataSource);
  const isRacApproved = vehicle?.dealer.partnerRac && tenant === 'rac';
  const size = tenant === 'purchasePro' ? 'pdp_purchasePro' : 'pdp';

  const handleImageClick = (imageIndex: number) => {
    if (totalImgs < 2) return;

    setCurrentClickImgIndex(imageIndex);
    setActiveTab(IMAGES_TAB_NAME);
    setStep(step + 1);
    window.location.hash = 'gallery';
  };
  const handleVideoBtnClick = () => {
    setActiveTab(VIDEO_TAB_NAME);
    setStep(step + 1);
    window.location.hash = 'gallery';
  };

  const handle360ImagesBtnClick = () => {
    setActiveTab(IMAGES_360_TAB_NAME);
  };

  const handleRequestBtnClick = (leadType: 'request-video' | 'request-images') => {
    const {
      leadStart: { event, context },
    } = generateLeadTrackingData({
      searchParams,
      userId,
      formState,
      leadFormId: leadType,
      leadId: '',
      listingId,
      vehicle,
      ctaLocation: 'pdp',
      leadAction: '',
    });

    trackCustomEvent({ event, context });
    toggleLeadForm(leadType);
  };

  useMemo(() => {
    if (is360Priority) setActiveTab(IMAGES_360_TAB_NAME);
  }, [is360Priority]);

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#gallery') {
        setStep(1);
      } else {
        setStep(0);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    setHasReqestCtas(
      !isVti && !isInstaMotionVehicle && (shouldRequestVideo || shouldRequestImages),
    );
  }, [isVti, isInstaMotionVehicle, shouldRequestVideo, shouldRequestImages]);

  const hasButtons = hasRequestCtas || hasVideo || has360Images;

  if (step === 0)
    return (
      <>
        {activeTab !== IMAGES_360_TAB_NAME && !is360Priority && (
          <ImageCarousel
            strings={imageCarouselStrings}
            images={images.length ? images : [FALLBACK_IMAGE]}
            comingSoonSlot={comingSoonSlot}
            imageOnClick={handleImageClick}
            positionIndex={currentClickImgIndex}
            index={0}
            isStockUnavailable={isStockUnavailable}
            carouselProps={carouselProps}
            shouldShowFavIcon
            shouldShowShare={tenant !== 'purchasePro'}
            isRacApproved={isRacApproved}
            isPdp
            tenant={tenant}
            size={size}
          />
        )}
        {activeTab === IMAGES_360_TAB_NAME && (
          <Image360 images={images360!} galleryStrings={galleryStrings} />
        )}

        {hasButtons && (
          <div
            className={`${styles.videoBtnWrapper} ${isStockUnavailable ? styles.addOpacity : ''}`}
          >
            {has360Images && activeTab !== IMAGES_TAB_NAME && !is360Priority && (
              <Button
                leftIcon={<ImageIcon />}
                onClick={() => setActiveTab(IMAGES_TAB_NAME)}
                aria-label="images button"
                className={styles.videoButton}
                disabled={isStockUnavailable}
              >
                <Typography variant="subheading3">Images</Typography>
              </Button>
            )}

            {has360Images && activeTab !== IMAGES_360_TAB_NAME && (
              <>
                <Button
                  leftIcon={<View360 />}
                  onClick={handle360ImagesBtnClick}
                  aria-label="360 video button"
                  className={styles.videoButton}
                  disabled={isStockUnavailable}
                >
                  <Typography variant="subheading3">{galleryStrings.view360}</Typography>
                </Button>
              </>
            )}
            {hasVideo && (
              <Button
                leftIcon={<Video />}
                onClick={handleVideoBtnClick}
                aria-label="video button"
                className={styles.videoButton}
                disabled={isStockUnavailable}
              >
                <Typography variant="subheading3">{galleryStrings.videoCta}</Typography>
              </Button>
            )}

            {!hasVideo && hasRequestCtas && (
              <Button
                leftIcon={<VideoReq />}
                onClick={() => handleRequestBtnClick('request-video')}
                aria-label="request video button"
                className={styles.videoButton}
                disabled={isStockUnavailable}
              >
                <Typography variant="subheading3">{galleryStrings.requestVideo}</Typography>
              </Button>
            )}

            {shouldRequestImages && hasRequestCtas && (
              <Button
                leftIcon={<ImageIcon />}
                onClick={() => handleRequestBtnClick('request-images')}
                aria-label="request images button"
                className={styles.videoButton}
                disabled={isStockUnavailable}
              >
                <Typography variant="subheading3">{galleryStrings.requestImages}</Typography>
              </Button>
            )}
          </div>
        )}
      </>
    );

  return (
    <GalleryContainer
      images={images}
      galleryStrings={galleryStrings}
      currentIndex={carouselProps.currentIndex}
      step={step}
      setStep={setStep}
      tenant={tenant}
    >
      {step === 1 && (
        <PatchworkGallery
          handleImageClick={handleImageClick}
          images={images}
          images360={images360}
          currentIndex={currentClickImgIndex}
          video={video}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          hasVideo={hasVideo}
          has360Images={has360Images}
          tabStrings={tabStrings}
          galleryStrings={galleryStrings}
          tenant={tenant}
        />
      )}

      {step === 2 && (
        <SlideshowGallery
          slideshowGalleryStrings={imageCarouselStrings}
          images={images}
          comingSoonSlot={comingSoonSlot}
          currentClickImgIndex={currentClickImgIndex}
          carouselProps={carouselProps}
          tenant={tenant}
        />
      )}
    </GalleryContainer>
  );
};

export default Gallery;
